// Where sensors are, set by the enterprise: a zip code, a placement, a
// category and a note, for one sensor, a handful, or a whole account at
// once. Kept with the enterprise (arcsrv, through POST /map/overlay), beside
// the owner's own settings and never over them; the map, the forecasts and
// the smart alerts read them first.
//
// Nothing is saved before a preview of every sensor it changes. What an
// owner set themselves is kept unless the person asks to replace it. Every
// save can be undone from its confirmation, or later from the change history.
import Modal from "../scripts/modal";
import htmlEntities from "../scripts/html-entities";
import { showToast } from "./toast";
import { NO_FILL } from "./no-autofill";

const esc = v => htmlEntities(v === undefined || v === null ? '' : String(v));

export const PLACEMENTS = [
  ['indoor', 'Indoors', 'heated or cooled'],
  ['unheated', 'Unheated', 'garage, crawl space, attic'],
  ['outdoor', 'Outdoors', ''],
  ['vehicle', 'Vehicle', 'truck, van, ambulance'],
];
export const PLACEMENT_NAMES = Object.fromEntries(PLACEMENTS.map(([id, name]) => [id, name]));

const FIELDS = ['zip', 'placement', 'category', 'notes'];
const FIELD_NAMES = { zip: 'Zip code', placement: 'Placement', category: 'Category', notes: 'Note' };
// what an owner can set in their own app; categories and notes are the enterprise's alone
const OWNER_FIELDS = ['zip', 'placement'];
const PREVIEW_ROWS = 150;

/** A US zip code's five digits, or a Canadian postal code as "A1A 1A1" ("A1A"); null when it's neither. */
export function normalizeZip(raw) {
  const s = String(raw || '').trim().toUpperCase();
  if(!s) return '';
  let m = /^(\d{5})(-?\d{4})?$/.exec(s);
  if(m) return m[1];
  m = /^([A-Z]\d[A-Z])\s*(\d[A-Z]\d)?$/.exec(s);
  if(m) return m[2] ? `${m[1]} ${m[2]}` : m[1];
  return null;
}

/** A field's value as shown: a placement in words. */
const shown = (field, v) => (!v ? '' : field === 'placement' ? (PLACEMENT_NAMES[v] || v) : v);

/**
 * The value in effect and who set it: the enterprise's, else the owner's.
 * sensor: { owner: { zip, placement }, ours: { zip, placement, category, notes } | null }
 */
export function inEffect(sensor, field) {
  const ours = sensor.ours && sensor.ours[field];
  if(ours) return { value: ours, by: 'ours' };
  const own = OWNER_FIELDS.includes(field) && sensor.owner ? sensor.owner[field] : '';
  return own ? { value: own, by: 'owner' } : { value: '', by: '' };
}

/**
 * What a save would do to each sensor. want: { field: { mode: 'set' | 'clear', value } }
 * (a field left out stays). replace: write over what owners set themselves.
 * Returns rows [{ sensor, fields: { field: { from, to, state } }, state }] where
 * a field's state is change, same or kept (the owner's), and the sensor's is
 * change when any field changes, else kept or same.
 */
export function previewOf(sensors, want, replace = false) {
  return sensors.map(sensor => {
    const fields = {};
    let state = 'same';
    Object.keys(want).forEach(field => {
      const w = want[field];
      const now = inEffect(sensor, field);
      const ours = (sensor.ours && sensor.ours[field]) || '';
      let f;
      if(w.mode === 'clear') {
        f = ours ? { from: ours, to: OWNER_FIELDS.includes(field) && sensor.owner ? sensor.owner[field] || '' : '', state: 'change', clear: true } : { from: now.value, to: now.value, state: 'same' };
      } else if(now.value === w.value) {
        f = { from: now.value, to: w.value, state: 'same' };
      } else if(now.by === 'owner' && !replace) {
        f = { from: now.value, to: now.value, state: 'kept' };
      } else {
        f = { from: now.value, to: w.value, state: 'change' };
      }
      fields[field] = f;
      if(f.state === 'change') state = 'change';
      else if(f.state === 'kept' && state !== 'change') state = 'kept';
    });
    return { sensor, fields, state };
  });
}

/** The edits a preview saves: only the fields that change, '' to clear. */
export function editsOf(rows) {
  return rows.filter(r => r.state === 'change').map(r => {
    const e = { sensor_id: r.sensor.id };
    Object.keys(r.fields).forEach(field => {
      const f = r.fields[field];
      if(f.state === 'change') e[field] = f.clear ? '' : f.to;
    });
    return e;
  });
}

/** "Zip 08809 and Unheated for 12 sensors" */
function summaryOf(want, n) {
  const parts = Object.keys(want).map(field => (want[field].mode === 'clear'
    ? `removed ${FIELD_NAMES[field].toLowerCase()}`
    : field === 'notes' ? 'a note' : field === 'zip' ? `zip ${want[field].value}` : shown(field, want[field].value)));
  const what = parts.join(', ');
  return `${what.charAt(0).toUpperCase()}${what.slice(1)} for ${n} sensor${n === 1 ? '' : 's'}`;
}

/** Save edits (POST /map/overlay); resolves the batch. */
export function saveSetup(edits, summary) {
  return Promise.resolve(window.apiRequest('post', '/map/overlay', { edits: JSON.stringify(edits), summary }, { maxTries: 1 }))
    .then(r => (r && r.type === 'success' ? r.data : Promise.reject(new Error((r && r.message) || 'Not saved. Please try again.'))));
}

/** Undo a save (POST /map/overlay/undo); resolves { restored, kept }. */
export function undoSetup(batch) {
  return Promise.resolve(window.apiRequest('post', '/map/overlay/undo', { batch }, { maxTries: 1 }))
    .then(r => (r && r.type === 'success' ? r.data : Promise.reject(new Error((r && r.message) || 'Not undone. Please try again.'))));
}

/**
 * Save, then say so with an Undo. onChange runs after the save and after an
 * undo (the page reloads what it shows).
 */
export function saveWithUndo(edits, summary, onChange) {
  return saveSetup(edits, summary).then(batch => {
    onChange && onChange(batch);
    if(!batch || !batch.id) {
      showToast('Nothing to change: already set that way.');
      return batch;
    }
    showToast(`Saved: ${summary}.`, {
      action: 'Undo',
      onAction: () => undoSetup(batch.id).then(res => {
        showToast(res.kept ? `Undone, except ${res.kept} value${res.kept === 1 ? '' : 's'} changed again since.` : 'Undone.');
        onChange && onChange(null);
      }).catch(err => showToast(err.message, { error: true })),
    });
    return batch;
  });
}

/**
 * The editor. sensors: [{ id (sensors.id), sensor_id, name, account,
 * account_id, owner: { zip, placement }, ours }]; title; focus: the field to
 * start in; preset: { field: value } filled in; categories: ones in use, to
 * pick from; onChange(batch): after a save or its undo.
 */
export function openSetupEditor({ sensors, title, focus = 'zip', preset = {}, categories = [], onChange } = {}) {
  if(!sensors || !sensors.length) return null;
  const accounts = [...new Set(sensors.map(s => s.account).filter(Boolean))];
  const where = accounts.length === 1 ? accounts[0] : `${accounts.length} accounts`;
  const n = sensors.length;
  const modal = new Modal({
    classes: ['form-modal', 'setup-editor-modal'],
    title: `${esc(title || (n === 1 ? `Where ${sensors[0].name || 'this sensor'} is` : `Where these ${n} sensors are`))} <span class="modal-header__subject">${esc(where)}</span>`,
  });
  const hasOurs = field => sensors.some(s => s.ours && s.ours[field]);
  const ownerHas = field => sensors.filter(s => inEffect(s, field).by === 'owner').length;
  const state = {};
  FIELDS.forEach(f => { if(preset[f] !== undefined) state[f] = { mode: 'set', value: preset[f] }; });
  const single = n === 1 ? sensors[0] : null;
  const current = f => (single ? inEffect(single, f) : { value: '', by: '' });
  const placeholder = f => {
    if(single) {
      const c = current(f);
      return c.value ? `${shown(f, c.value)} (${c.by === 'owner' ? 'the owner’s' : 'yours'}) · type to change` : 'Not set';
    }
    return 'Leave as is';
  };
  const clearBtn = f => (hasOurs(f) ? `<button type="button" class="setup-editor__clear" data-clear="${f}">Remove yours</button>` : '');

  modal.setContent(`
    <form class="setup-editor" novalidate autocomplete="off">
      <div class="setup-editor__fields">
        <label class="setup-editor__field">
          <span class="setup-editor__label">Zip code ${clearBtn('zip')}</span>
          <input type="text" name="se-loc" data-field="zip" ${NO_FILL} inputmode="text" maxlength="10" placeholder="${esc(placeholder('zip'))}" value="${esc(preset.zip || '')}">
          <span class="setup-editor__hint" data-hint="zip">US zip or Canadian postal code. Puts the sensor on the map and its weather in its forecasts.</span>
        </label>
        <div class="setup-editor__field" role="group" aria-label="Placement">
          <span class="setup-editor__label">Placement ${clearBtn('placement')}</span>
          <div class="setup-editor__chips">
            <button type="button" class="setup-editor__chip${state.placement ? '' : ' is-on'}" data-place="">Leave as is</button>
            ${PLACEMENTS.map(([id, name, hint]) => `<button type="button" class="setup-editor__chip${state.placement && state.placement.value === id ? ' is-on' : ''}" data-place="${id}" title="${esc(hint)}">${esc(name)}</button>`).join('')}
          </div>
          <span class="setup-editor__hint">${single && current('placement').value ? `Now: ${esc(shown('placement', current('placement').value))} (${current('placement').by === 'owner' ? 'the owner’s' : 'yours'}). ` : ''}How much the outside air reaches it: the freeze forecast weighs it.</span>
        </div>
        <label class="setup-editor__field">
          <span class="setup-editor__label">Category ${clearBtn('category')}</span>
          <input type="text" name="se-category" data-field="category" maxlength="60" list="setup-editor-categories" placeholder="${esc(placeholder('category'))}" value="${esc(preset.category || '')}" ${NO_FILL}>
          <datalist id="setup-editor-categories">${categories.map(c => `<option value="${esc(c)}">`).join('')}</datalist>
          <span class="setup-editor__hint">What it watches, in your words: “Walk-in freezer”, “Crawl space pipes”. The map filters by it.</span>
        </label>
        <label class="setup-editor__field">
          <span class="setup-editor__label">Note ${clearBtn('notes')}</span>
          <textarea name="se-notes" data-field="notes" rows="2" maxlength="500" placeholder="${esc(placeholder('notes'))}" ${NO_FILL}>${esc(preset.notes || '')}</textarea>
        </label>
      </div>
      <label class="setup-editor__owner" hidden><input type="checkbox" name="replace"> <span></span></label>
      <div class="setup-editor__preview" aria-live="polite"></div>
      <p class="setup-editor__note">Kept with your enterprise. The owner’s own settings in their Temp Stick app don’t change. You can undo this after saving.</p>
      <div class="form-modal-actions">
        <div class="form-modal-actions__submit">
          <button type="submit" class="button" data-for="okay" disabled>Save</button>
          <button type="button" class="button gray" data-for="cancel">Cancel</button>
          <div class="error-message"></div>
        </div>
      </div>
    </form>`);

  const root = modal.getContainer();
  const form = root.querySelector('form');
  const preview = form.querySelector('.setup-editor__preview');
  const ownerBox = form.querySelector('.setup-editor__owner');
  const save = form.querySelector('[data-for="okay"]');
  const error = form.querySelector('.error-message');
  let rows = [];

  const draw = () => {
    error.classList.remove('is-visible');
    // a zip that isn't one says so where it's typed
    const zipHint = form.querySelector('[data-hint="zip"]');
    const zipBad = state.zip && state.zip.mode === 'set' && state.zip.value === null;
    zipHint.classList.toggle('is-bad', !!zipBad);
    zipHint.textContent = zipBad ? 'That isn’t a US zip code or a Canadian postal code.' : 'US zip or Canadian postal code. Puts the sensor on the map and its weather in its forecasts.';
    const want = {};
    Object.keys(state).forEach(f => {
      const s = state[f];
      if(s.mode === 'clear' || (s.mode === 'set' && s.value)) want[f] = s;
    });
    form.querySelectorAll('[data-clear]').forEach(b => {
      const on = state[b.dataset.clear] && state[b.dataset.clear].mode === 'clear';
      b.classList.toggle('is-on', on);
      b.textContent = on ? 'Will remove yours · keep' : 'Remove yours';
    });
    // the owners' own values in the way: kept unless asked
    const owned = Object.keys(want).filter(f => want[f].mode === 'set' && OWNER_FIELDS.includes(f))
      .reduce((m, f) => Math.max(m, sensors.filter(s => inEffect(s, f).by === 'owner' && inEffect(s, f).value !== want[f].value).length), 0);
    ownerBox.hidden = !owned;
    ownerBox.querySelector('span').innerHTML = `Also replace what owners set themselves <small>(${owned} sensor${owned === 1 ? ' has its' : 's have their'} own ${Object.keys(want).filter(f => OWNER_FIELDS.includes(f)).map(f => FIELD_NAMES[f].toLowerCase()).join(' or ')})</small>`;
    const replace = !ownerBox.hidden && form.querySelector('[name="replace"]').checked;
    if(!Object.keys(want).length || zipBad) {
      rows = [];
      preview.innerHTML = n > 1 ? `<p class="setup-editor__empty">Fill in what to set: only those fields change, on all ${n} sensors.</p>` : '';
      save.disabled = true;
      save.textContent = 'Save';
      return;
    }
    rows = previewOf(sensors, want, replace);
    const count = st => rows.filter(r => r.state === st).length;
    const changing = count('change');
    const cols = Object.keys(want);
    const cell = (f, x) => {
      if(!x) return '<td></td>';
      if(x.state === 'kept') return `<td class="is-kept"><span>${esc(shown(f, x.from))}</span><small>owner’s, kept</small></td>`;
      if(x.state === 'same') return `<td class="is-same">${esc(shown(f, x.to)) || '<i>none</i>'}</td>`;
      return `<td class="is-change">${x.from ? `<s>${esc(shown(f, x.from))}</s> ` : ''}<b>${esc(shown(f, x.to)) || '<i>none</i>'}</b></td>`;
    };
    const shownRows = rows.slice().sort((a, b) => ['change', 'kept', 'same'].indexOf(a.state) - ['change', 'kept', 'same'].indexOf(b.state)).slice(0, PREVIEW_ROWS);
    preview.innerHTML = `
      <div class="setup-editor__tally">
        <span class="is-change"><b>${changing}</b> will change</span>
        ${count('kept') ? `<span class="is-kept"><b>${count('kept')}</b> keep the owner’s</span>` : ''}
        ${count('same') ? `<span class="is-same"><b>${count('same')}</b> already set</span>` : ''}
      </div>
      <div class="setup-editor__table"><table>
        <thead><tr><th>Sensor</th>${cols.map(f => `<th>${esc(FIELD_NAMES[f])}</th>`).join('')}</tr></thead>
        <tbody>${shownRows.map(r => `<tr class="is-${r.state}"><th scope="row">${esc(r.sensor.name)}<small>${esc(accounts.length > 1 ? r.sensor.account : r.sensor.sensor_id || '')}</small></th>${cols.map(f => cell(f, r.fields[f])).join('')}</tr>`).join('')}</tbody>
      </table>${rows.length > PREVIEW_ROWS ? `<p class="setup-editor__more">and ${rows.length - PREVIEW_ROWS} more</p>` : ''}</div>`;
    save.disabled = !changing;
    save.textContent = changing ? `Save ${changing} sensor${changing === 1 ? '' : 's'}` : 'Nothing to change';
  };

  const typed = (f, raw) => {
    const v = f === 'zip' ? normalizeZip(raw) : f === 'category' ? raw.replace(/\s+/g, ' ').trim() : raw.trim();
    if(!String(raw).trim()) delete state[f];
    else state[f] = { mode: 'set', value: v };
    draw();
  };
  form.addEventListener('input', e => {
    const f = e.target.dataset.field || e.target.name;
    if(['zip', 'category', 'notes'].includes(f)) typed(f, e.target.value);
    if(f === 'replace') draw();
  });
  form.addEventListener('change', e => { if(e.target.name === 'replace') draw(); });
  form.addEventListener('click', e => {
    const chip = e.target.closest('[data-place]');
    if(chip) {
      form.querySelectorAll('[data-place]').forEach(c => c.classList.toggle('is-on', c === chip));
      if(chip.dataset.place) state.placement = { mode: 'set', value: chip.dataset.place };
      else delete state.placement;
      draw();
      return;
    }
    const clear = e.target.closest('[data-clear]');
    if(clear) {
      const f = clear.dataset.clear;
      if(state[f] && state[f].mode === 'clear') delete state[f];
      else {
        state[f] = { mode: 'clear' };
        const input = form.querySelector(`[data-field="${f}"]`);
        if(input) input.value = '';
        if(f === 'placement') form.querySelectorAll('[data-place]').forEach(c => c.classList.toggle('is-on', !c.dataset.place));
      }
      draw();
      return;
    }
    if(e.target.closest('[data-for="cancel"]')) modal.close();
  });
  form.addEventListener('submit', e => {
    e.preventDefault();
    const edits = editsOf(rows);
    if(!edits.length) return;
    const want = {};
    Object.keys(state).forEach(f => { if(state[f].mode === 'clear' || state[f].value) want[f] = state[f]; });
    save.disabled = true;
    save.textContent = 'Saving…';
    saveWithUndo(edits, summaryOf(want, edits.length), onChange)
      .then(() => modal.close())
      .catch(err => {
        error.textContent = err.message;
        error.classList.add('is-visible');
        draw();
      });
  });

  modal.open();
  draw();
  const start = form.querySelector(`[data-field="${focus}"]`);
  if(start) start.focus();
  return modal;
}

/**
 * The change history: every save, newest first, who and when, with Undo on
 * the ones not undone yet. onChange: after an undo.
 */
export function openSetupHistory({ onChange } = {}) {
  const modal = new Modal({ classes: ['form-modal', 'setup-editor-modal'], title: 'Sensor settings: change history' });
  modal.setContent('<div class="setup-history"><p class="setup-editor__empty">Loading…</p></div>');
  const host = modal.getContainer().querySelector('.setup-history');
  const when = t => {
    const d = new Date(String(t).replace(' ', 'T') + (/Z|[+-]\d\d:?\d\d$/.test(t) ? '' : 'Z'));
    return isFinite(d) ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  };
  const load = () => Promise.resolve(window.apiRequest('get', '/map/overlay/log?limit=50', {}, { maxTries: 1 })).then(r => {
    const d = r && r.type === 'success' ? r.data : null;
    if(!d || !d.available) {
      host.innerHTML = '<p class="setup-editor__empty">The change history isn’t available right now.</p>';
      return;
    }
    if(!d.batches.length) {
      host.innerHTML = '<p class="setup-editor__empty">No changes yet. Saves from the map, the Dashboard and Accounts show up here.</p>';
      return;
    }
    host.innerHTML = `<ol class="setup-history__list">${d.batches.map(b => `
      <li class="${b.undone_at ? 'is-undone' : ''}">
        <span class="setup-history__what"><b>${esc(b.summary || `${b.sensors} sensors`)}</b><small>${esc(b.by_name || 'Someone')} · ${esc(when(b.at))}${b.undone_at ? ` · undone${b.undone_by ? ` by ${esc(b.undone_by)}` : ''}` : ''}</small></span>
        ${b.undone_at ? '' : `<button type="button" class="button button--small copy-link" data-undo="${b.id}">Undo</button>`}
      </li>`).join('')}</ol>`;
  });
  host.addEventListener('click', e => {
    const b = e.target.closest('[data-undo]');
    if(!b) return;
    b.disabled = true;
    b.textContent = 'Undoing…';
    undoSetup(Number(b.dataset.undo)).then(res => {
      showToast(res.kept ? `Undone, except ${res.kept} value${res.kept === 1 ? '' : 's'} changed again since.` : 'Undone.');
      onChange && onChange(null);
      load();
    }).catch(err => {
      showToast(err.message, { error: true });
      load();
    });
  });
  modal.open();
  load();
  return modal;
}

/** The before/after table: rows from previewOf (a line number on each when line). */
export function previewTableHtml(rows, cols, { line = false, many = true } = {}) {
  const cell = (f, x) => {
    if(!x) return '<td class="is-same"></td>';
    if(x.state === 'kept') return `<td class="is-kept"><span>${esc(shown(f, x.from))}</span><small>owner’s, kept</small></td>`;
    if(x.state === 'same') return `<td class="is-same">${esc(shown(f, x.to)) || '<i>none</i>'}</td>`;
    return `<td class="is-change">${x.from ? `<s>${esc(shown(f, x.from))}</s> ` : ''}<b>${esc(shown(f, x.to)) || '<i>none</i>'}</b></td>`;
  };
  const order = ['change', 'kept', 'same'];
  const list = rows.slice().sort((a, b) => order.indexOf(a.state) - order.indexOf(b.state) || (a.line || 0) - (b.line || 0));
  const count = st => rows.filter(r => r.state === st).length;
  return `<div class="setup-editor__tally">
      <span class="is-change"><b>${count('change').toLocaleString('en-US')}</b> will change</span>
      ${count('kept') ? `<span class="is-kept"><b>${count('kept')}</b> keep the owner’s</span>` : ''}
      ${count('same') ? `<span class="is-same"><b>${count('same')}</b> already set</span>` : ''}
    </div>
    <div class="setup-editor__table"><table>
      <thead><tr>${line ? '<th>Line</th>' : ''}<th>Sensor</th>${cols.map(f => `<th>${esc(FIELD_NAMES[f])}</th>`).join('')}</tr></thead>
      <tbody>${list.slice(0, PREVIEW_ROWS).map(r => `<tr class="is-${r.state}">${line ? `<td class="is-same">${r.line}</td>` : ''}<th scope="row">${esc(r.sensor.name)}<small>${esc(many ? r.sensor.account || '' : r.sensor.sensor_id || '')}</small></th>${cols.map(f => cell(f, r.fields[f])).join('')}</tr>`).join('')}</tbody>
    </table>${rows.length > PREVIEW_ROWS ? `<p class="setup-editor__more">and ${(rows.length - PREVIEW_ROWS).toLocaleString('en-US')} more</p>` : ''}</div>`;
}

/**
 * Review values that differ sensor by sensor (a zip each, typed in a list)
 * before saving them as one change. items: [{ sensor (the editor's form),
 * want: { field: { mode, value } } }]; summary: the change's name.
 */
export function openRowsReview({ items, title, summary, onChange } = {}) {
  if(!items || !items.length) return null;
  const accounts = [...new Set(items.map(i => i.sensor.account).filter(Boolean))];
  const modal = new Modal({
    classes: ['form-modal', 'setup-editor-modal'],
    title: `${esc(title || `Review ${items.length} sensor${items.length === 1 ? '' : 's'}`)} <span class="modal-header__subject">${esc(accounts.length === 1 ? accounts[0] : `${accounts.length} accounts`)}</span>`,
  });
  modal.setContent(`<form class="setup-editor" novalidate>
      <label class="setup-editor__owner" hidden><input type="checkbox" name="replace"> <span></span></label>
      <div class="setup-editor__preview"></div>
      <p class="setup-editor__note">Kept with your enterprise. The owner’s own settings in their Temp Stick app don’t change. You can undo this after saving.</p>
      <div class="form-modal-actions"><div class="form-modal-actions__submit">
        <button type="submit" class="button" data-for="okay" disabled>Save</button>
        <button type="button" class="button gray" data-for="cancel">Cancel</button>
        <div class="error-message"></div>
      </div></div>
    </form>`);
  const form = modal.getContainer().querySelector('form');
  const owner = form.querySelector('.setup-editor__owner');
  const save = form.querySelector('[data-for="okay"]');
  const error = form.querySelector('.error-message');
  const cols = FIELDS.filter(f => items.some(i => i.want[f]));
  let rows = [];
  const draw = () => {
    const replace = form.querySelector('[name="replace"]').checked;
    rows = items.map(i => previewOf([i.sensor], i.want, replace)[0]);
    const owned = items.filter(i => Object.keys(i.want).some(f => i.want[f].mode === 'set' && OWNER_FIELDS.includes(f)
      && inEffect(i.sensor, f).by === 'owner' && inEffect(i.sensor, f).value !== i.want[f].value)).length;
    owner.hidden = !owned;
    owner.querySelector('span').innerHTML = `Also replace what owners set themselves <small>(${owned} sensor${owned === 1 ? ' has its' : 's have their'} own)</small>`;
    form.querySelector('.setup-editor__preview').innerHTML = previewTableHtml(rows, cols, { many: accounts.length > 1 });
    const changing = rows.filter(r => r.state === 'change').length;
    save.disabled = !changing;
    save.textContent = changing ? `Save ${changing} sensor${changing === 1 ? '' : 's'}` : 'Nothing to change';
  };
  form.addEventListener('change', e => { if(e.target.name === 'replace') draw(); });
  form.addEventListener('click', e => { if(e.target.closest('[data-for="cancel"]')) modal.close(); });
  form.addEventListener('submit', e => {
    e.preventDefault();
    const edits = editsOf(rows);
    if(!edits.length) return;
    save.disabled = true;
    save.textContent = 'Saving…';
    saveWithUndo(edits, summary || `${edits.length} sensor${edits.length === 1 ? '' : 's'} set one by one`, onChange)
      .then(() => modal.close())
      .catch(err => {
        error.textContent = err.message;
        error.classList.add('is-visible');
        draw();
      });
  });
  modal.open();
  draw();
  return modal;
}
